import React, { useCallback, useEffect, useState } from 'react';
import {
  FiAlertTriangle,
  FiArrowRight,
  FiCheckCircle,
  FiFileText,
  FiRepeat,
  FiShield,
} from 'react-icons/fi';
import {
  clientPortalService,
  type ClientPortalActiveGuard,
  type ClientPortalContext,
  type ClientPortalReport,
  type ClientPortalScope,
  type ClientPortalPatrolSweep,
} from '../../services/clientPortalService';
import { getReportTitle, reportTypeLabelFor } from '../../utils/reportPresentation';
import { useI18n } from '../../i18n';
import type { ClientPortalSection } from '../../components/ClientPortalShell';
import { formatDateTime, formatRelative } from './clientPortalFormat';
import './ClientPortal.css';

interface Props {
  context: ClientPortalContext;
  siteId: string | null;
  can: (scope: ClientPortalScope) => boolean;
  onNavigate: (section: ClientPortalSection) => void;
}

const INCIDENT_TYPES = ['incident', 'use_of_force', 'alarm'];

export default function ClientOverviewPage({ context, siteId, can, onNavigate }: Props) {
  const { t } = useI18n();
  const [activeGuards, setActiveGuards] = useState<ClientPortalActiveGuard[]>([]);
  const [recentReports, setRecentReports] = useState<ClientPortalReport[]>([]);
  const [sweeps, setSweeps] = useState<ClientPortalPatrolSweep[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000);
      const [guards, reports, progress] = await Promise.all([
        can('active_guards') ? clientPortalService.fetchActiveGuards() : Promise.resolve([]),
        clientPortalService.fetchReports({ siteId, limit: 6 }),
        can('patrol_progress')
          ? clientPortalService.fetchPatrolProgress(since, new Date(), siteId)
          : Promise.resolve([]),
      ]);
      setActiveGuards(siteId ? guards.filter((guard) => guard.siteId === siteId) : guards);
      setRecentReports(reports);
      setSweeps(progress);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [can, siteId]);

  useEffect(() => {
    void load();
  }, [load]);

  const openIncidents = recentReports.filter(
    (report) => INCIDENT_TYPES.includes((report.report_type ?? '').toLowerCase()) && report.status !== 'closed',
  ).length;
  const completedSweeps = sweeps.filter((sweep) => sweep.status === 'completed').length;
  const missedSweeps = sweeps.filter((sweep) => sweep.status === 'missed').length;

  if (isLoading) {
    return <div className="cp-loading"><div className="spinner" /></div>;
  }

  return (
    <div className="cp-page">
      {error ? <div className="cp-banner cp-banner--error">{error}</div> : null}

      <section className="cp-stat-row">
        <article className="cp-stat">
          <FiShield aria-hidden="true" />
          <span>{t('Officers on duty')}</span>
          <strong>{activeGuards.length}</strong>
          <small>
            {can('active_guards')
              ? t('Across {count} sites', { count: context.sites.length })
              : t('Not enabled on your account')}
          </small>
        </article>

        <article className="cp-stat">
          <FiRepeat aria-hidden="true" />
          <span>{t('Patrols completed')}</span>
          <strong>{completedSweeps}</strong>
          <small>{t('Last 24 hours')}</small>
        </article>

        <article className={`cp-stat${missedSweeps > 0 ? ' cp-stat--warn' : ''}`}>
          <FiAlertTriangle aria-hidden="true" />
          <span>{t('Missed sweeps')}</span>
          <strong>{missedSweeps}</strong>
          <small>{t('Last 24 hours')}</small>
        </article>

        <article className={`cp-stat${openIncidents > 0 ? ' cp-stat--alert' : ''}`}>
          <FiFileText aria-hidden="true" />
          <span>{t('Open incidents')}</span>
          <strong>{openIncidents}</strong>
          <small>{t('Awaiting closure')}</small>
        </article>
      </section>

      <div className="cp-grid cp-grid--2">
        <article className="cp-panel">
          <header className="cp-panel__head">
            <div>
              <h2>{t('On duty now')}</h2>
              <p>{t('Officers currently clocked in at your property.')}</p>
            </div>
            {can('active_guards') ? (
              <button type="button" className="cp-link" onClick={() => onNavigate('live')}>
                {t('Live operations')} <FiArrowRight aria-hidden="true" />
              </button>
            ) : null}
          </header>

          {!can('active_guards') ? (
            <div className="cp-empty">{t('Your security provider has not enabled on-duty visibility.')}</div>
          ) : activeGuards.length === 0 ? (
            <div className="cp-empty">{t('No officers are clocked in right now.')}</div>
          ) : (
            <ul className="cp-list">
              {activeGuards.slice(0, 5).map((guard) => (
                <li key={guard.shiftExecutionId} className="cp-list__row">
                  <div>
                    <strong>{guard.guardName ?? t('Officer on duty')}</strong>
                    <span>{guard.siteName}</span>
                  </div>
                  <div className="cp-list__meta">
                    <span className="cp-chip cp-chip--live">{t('On duty')}</span>
                    <span>{t('Since {time}', { time: formatDateTime(guard.actualClockInAt) })}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </article>

        <article className="cp-panel">
          <header className="cp-panel__head">
            <div>
              <h2>{t('Latest reports')}</h2>
              <p>{t('The most recent submissions released to you.')}</p>
            </div>
            <button type="button" className="cp-link" onClick={() => onNavigate('reports')}>
              {t('All reports')} <FiArrowRight aria-hidden="true" />
            </button>
          </header>

          {recentReports.length === 0 ? (
            <div className="cp-empty">{t('No reports have been released to your portal yet.')}</div>
          ) : (
            <ul className="cp-list">
              {recentReports.slice(0, 5).map((report) => (
                <li key={report.id} className="cp-list__row">
                  <div>
                    <strong>{getReportTitle(report)}</strong>
                    <span>{reportTypeLabelFor(report.report_type)} · {report.sites?.name ?? t('Unknown site')}</span>
                  </div>
                  <div className="cp-list__meta">
                    <span>{formatRelative(report.created_at)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </article>
      </div>

      <article className="cp-panel">
        <header className="cp-panel__head">
          <div>
            <h2>{t('Recent patrol activity')}</h2>
            <p>{t('Sweeps recorded across your sites in the last 24 hours.')}</p>
          </div>
          {can('patrol_records') ? (
            <button type="button" className="cp-link" onClick={() => onNavigate('tours')}>
              {t('Tour records')} <FiArrowRight aria-hidden="true" />
            </button>
          ) : null}
        </header>

        {!can('patrol_progress') ? (
          <div className="cp-empty">{t('Your security provider has not enabled patrol visibility.')}</div>
        ) : sweeps.length === 0 ? (
          <div className="cp-empty">{t('No patrols were scheduled in the last 24 hours.')}</div>
        ) : (
          <ul className="cp-list">
            {sweeps.slice(0, 6).map((sweep) => (
              <li key={sweep.segmentId} className="cp-list__row">
                <div>
                  <strong>{sweep.patrolName ?? t('Patrol')}</strong>
                  <span>{sweep.siteName}</span>
                </div>
                <div className="cp-list__meta">
                  <span className={`cp-chip cp-chip--${sweep.status}`}>
                    {sweep.status === 'completed' ? <FiCheckCircle aria-hidden="true" /> : null}
                    {sweep.status === 'completed'
                      ? t('Completed')
                      : sweep.status === 'missed'
                        ? t('Missed')
                        : sweep.status === 'in_progress'
                          ? t('In progress')
                          : t('Scheduled')}
                  </span>
                  <span>
                    {t('{done}/{total} checkpoints', {
                      done: sweep.checkpointsCompleted,
                      total: sweep.checkpointsTotal,
                    })}
                  </span>
                  <span>{formatDateTime(sweep.plannedStartAt)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </article>
    </div>
  );
}
